/*
 **	iOS-style Slide-up Panel Component
 **	OMNI_IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 */

import React, { useEffect, useState } from 'react'
import { OCAColors, Typography, Spacing, BorderRadius } from '../../theme/colors'

interface IOSSlideUpPanelProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  height?: string
  headerAction?: React.ReactNode
}

export const IOSSlideUpPanel: React.FC<IOSSlideUpPanelProps> = ({
  isOpen,
  onClose,
  title,
  children,
  height = '80vh',
  headerAction
}) => {
  const [isAnimating, setIsAnimating] = useState(false)
  const [shouldRender, setShouldRender] = useState(false)
  const [startY, setStartY] = useState<number | null>(null)
  const [currentY, setCurrentY] = useState<number | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const contentRef = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true)
      // Trigger animation after render
      setTimeout(() => setIsAnimating(true), 10)
      // Reset scroll position when opening
      if (contentRef.current) {
        contentRef.current.scrollTop = 0
      }
    } else {
      setIsAnimating(false)
      // Hide after animation completes
      setTimeout(() => setShouldRender(false), 300)
    }
  }, [isOpen])

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0]
    const target = e.target as HTMLElement

    // Only start dragging if we're at the top of the scroll and touching the header area
    if (contentRef.current && contentRef.current.scrollTop === 0 && target.closest('[data-drag-handle]')) {
      setStartY(touch.clientY)
      setCurrentY(touch.clientY)
      setIsDragging(true)
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || startY === null) return

    const touch = e.touches[0]
    const deltaY = touch.clientY - startY

    // Only allow dragging down
    if (deltaY > 0) {
      setCurrentY(touch.clientY)
      e.preventDefault()
    } else {
      // Cancel dragging if moving up
      setIsDragging(false)
      setStartY(null)
      setCurrentY(null)
    }
  }

  const handleTouchEnd = () => {
    if (!isDragging || startY === null || currentY === null) {
      setIsDragging(false)
      setStartY(null)
      setCurrentY(null)
      return
    }

    const deltaY = currentY - startY
    const threshold = 150 // Increased threshold to prevent accidental dismissal

    if (deltaY > threshold) {
      onClose()
    }

    setIsDragging(false)
    setStartY(null)
    setCurrentY(null)
  }

  const getTransform = () => {
    if (!isDragging || !isAnimating || startY === null || currentY === null) {
      return isAnimating ? 'translateY(0)' : 'translateY(100%)'
    }

    const deltaY = Math.max(0, currentY - startY) // Only allow downward movement
    return `translateY(${deltaY}px)`
  }

  if (!shouldRender) return null

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        backgroundColor: isAnimating ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0)',
        transition: 'background-color 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        backdropFilter: isAnimating ? 'blur(20px)' : 'blur(0px)',
        WebkitBackdropFilter: isAnimating ? 'blur(20px)' : 'blur(0px)',
      }}
      onClick={handleBackdropClick}
    >
      {/* Panel Container */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height,
          maxHeight: '100vh',
          background: 'rgba(242, 242, 247, 0.95)',
          backdropFilter: 'blur(40px)',
          WebkitBackdropFilter: 'blur(40px)',
          borderTopLeftRadius: BorderRadius.xl,
          borderTopRightRadius: BorderRadius.xl,
          transform: getTransform(),
          transition: isDragging ? 'none' : 'transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0px -2px 32px rgba(0, 0, 0, 0.15), 0px -1px 8px rgba(0, 0, 0, 0.1)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/* Draggable Header Area */}
        <div
          data-drag-handle
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            cursor: 'grab',
            touchAction: 'none',
          }}
        >
          {/* Handle Bar */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            padding: `${Spacing.xs} 0`,
            paddingTop: '12px',
            backgroundColor: 'transparent',
          }}>
            <div style={{
              width: '36px',
              height: '3px',
              backgroundColor: 'rgba(60, 60, 67, 0.3)',
              borderRadius: '2px',
            }} />
          </div>

          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: headerAction ? 'space-between' : 'center',
            padding: `${Spacing.sm} ${Spacing.lg} ${Spacing.md}`,
            backgroundColor: 'transparent',
          }}>
            <h2 style={{
              fontSize: Typography.fontSize.lg,
              fontWeight: Typography.fontWeight.semibold,
              color: OCAColors.label,
              margin: 0,
            }}>
              {title}
            </h2>
            {headerAction && (
              <div style={{
                marginLeft: Spacing.md,
              }}>
                {headerAction}
              </div>
            )}
          </div>
        </div>

        {/* Scrollable Content */}
        <div
          ref={contentRef}
          style={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: 'transparent',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  )
}